import React from 'react'
import moment from 'moment'
import Grid from '@mui/material/Grid'
import ActionButton from '@/components/buttons/ActionButton'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'
import { useSwipeable } from 'react-swipeable'
import { IValue } from '@/components/dropDown/DropDown'
import PopupDialog from './PopupDialog'
import { ISelection } from '@/components/listSelection/ListSelection'
import Button from '@mui/material/Button'
import { getWindowDimensions, windowsWidth } from '@/utils/screenUtils'
import CalendarHeader from './CalendarHeader'
import CalendarDayComments, { ICalendarDayComment } from './CalendarDayComments'
import ScaleColumn from './ScaleColumn'
import CalendarBody from './CalendarBody'
import Event from './Event'
import utils from './utils'
import {
  IScaleInterval,
  IExistingScaleInterval,
  ICellConfig,
  EDragType,
  ECellColor,
  IExceptions,
} from './calendarTypes'
import '@/css/calendar.less'

interface IPos {
  x: number
  y: number
}

interface ICalendarProps {
  onIntervalSelect(
    startTimestamp: Date,
    endTimestamp: Date,
    comment: string,
    placeId: number,
    exceptions?: IExceptions,
  ): void
  onIntervalUpdate(
    id: number,
    startTimestamp: Date,
    endTimestamp: Date,
    comment: string,
    placeId: number,
  ): void
  onIntervalsRemove(ids: number[]): void
  onChangeDayComment?(date: Date, value: string): void
  onFirstDayUpdate?(newFirstDay: moment.Moment): void
  preview?: boolean
  selections?: ISelection[]
  isLoading: boolean
  selectedIntervals: IExistingScaleInterval[]
  underlyingCellConfigs: ICellConfig[]
  placeData?: IValue[]
  outsideRangeColor?: ECellColor
  firstDay: moment.Moment
  minDay?: moment.Moment
  maxDay?: moment.Moment
  numberOfDays?: number
  scaleUnit?: number
  eventSpacing?: number
  cellHeight?: number
  scaleFormat?: 'HH:mm'
  startTime?: moment.Moment
  endTime?: moment.Moment
  calendarDayComment: ICalendarDayComment[]
  createColor: ECellColor
  allowOverlap?: boolean
  showPlaceOnEvent?: boolean
}

const Calendar: React.FC<ICalendarProps> = ({
  onIntervalSelect,
  onIntervalUpdate,
  onIntervalsRemove,
  onFirstDayUpdate,
  onChangeDayComment,
  isLoading,
  preview,
  selections,
  underlyingCellConfigs,
  placeData,
  outsideRangeColor,
  selectedIntervals,
  firstDay,
  minDay,
  maxDay,
  numberOfDays = 7,
  scaleUnit = 30,
  eventSpacing = 8,
  cellHeight = 30,
  scaleFormat = 'HH:mm',
  startTime = moment({ h: 8, m: 0 }),
  endTime = moment({ h: 22, m: 0 }),
  calendarDayComment,
  createColor,
  allowOverlap,
  showPlaceOnEvent,
}) => {
  const correctedCellConfigs = underlyingCellConfigs.map((cellConfig) => {
    // Check if going over to or from daylight saving time.
    const correctedStart = cellConfig.startTimestamp.clone()
    if (!firstDay.isDST() && correctedStart.isDST()) {
      correctedStart.subtract(1, 'hours')
    } else if (firstDay.isDST() && !correctedStart.isDST()) {
      correctedStart.add(1, 'hours')
    }
    return {
      ...cellConfig,
      startTimestamp: correctedStart,
    }
  })
  const { t } = useTranslation('default')
  const scaleIntervals = utils.getIntervalsByDuration(
    scaleUnit,
    startTime,
    endTime,
  )
  const offsetTop = utils.getOffset(scaleIntervals[0].start)
  const [columnDimensions, setColumnDimensions] = React.useState([])
  const [startSelectionPosition, setStartSelectionPosition] = React.useState<
    IPos | undefined
  >()
  const [startDay, setStartDay] = React.useState<moment.Moment | undefined>()
  const [mousePosition, setMousePosition] = React.useState<IPos>({ x: 0, y: 0 })
  const [preselectedInterval, setPreselectedInterval] =
    React.useState<IScaleInterval>()
  const [selectedEvent, setSelectedEvent] = React.useState<
    IExistingScaleInterval | undefined
  >()
  const [selectedPopup, setSelectedPopup] = React.useState<
    'ADD' | 'EDIT' | undefined
  >()
  const [dragType, setDragType] = React.useState<EDragType | undefined>()
  const [cellWidth, setCellWidth] = React.useState<number>(0)
  const [minMaxInterval, setMinMaxInterval] = React.useState<IScaleInterval>()
  const [selectedIntervalsState, setSelectedIntervalsState] =
    React.useState<IExistingScaleInterval[]>(selectedIntervals)
  let fontSize = '1em'
  const dayCommentHeight = cellHeight * 4
  const mainOffsetTop = dayCommentHeight + cellHeight

  React.useEffect(() => {
    if (isLoading === false) {
      setPreselectedInterval(undefined)
      setSelectedPopup(undefined)
      setStartSelectionPosition(undefined)
      setStartDay(undefined)
    }
  }, [selectedIntervals])

  const [windowDimensions, setWindowDimensions] = React.useState(
    getWindowDimensions(),
  )
  React.useEffect(() => {
    const handleResize = () => {
      setWindowDimensions(getWindowDimensions())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  const isMobileWidth = windowDimensions.width < windowsWidth

  React.useEffect(() => {
    calculateColumnDimension()
    window.addEventListener('resize', () => {
      calculateColumnDimension()
    })
    return () =>
      window.removeEventListener('resize', () => {
        calculateColumnDimension()
      })
  }, [])

  React.useEffect(() => {
    setSelectedIntervalsState(selectedIntervals)
  }, [selectedIntervals])

  if (cellWidth < 100) {
    fontSize = '0.8em'
  }

  const calculateColumnDimension = () => {
    const element = document.getElementById('weekCalendar')
    const containerWidth = element?.getBoundingClientRect().width
    const newCellWidth =
      Math.round((containerWidth / (numberOfDays + 1)) * 10) / 10
    setCellWidth(newCellWidth)

    const newColumnDimensions = []
    for (let i = 0; i < numberOfDays; i += 1) {
      const left: number =
        i === 0
          ? newCellWidth
          : newColumnDimensions[i - 1].left + newColumnDimensions[i - 1].width
      newColumnDimensions.push({
        left,
        width: newCellWidth,
      })
    }
    setColumnDimensions(newColumnDimensions)
  }

  const calculateDrag = (mousePos: IPos): IExistingScaleInterval => {
    const minutesStep = 30
    if (dragType === EDragType.Top) {
      let newDate = utils.getMoment(
        selectedEvent.start,
        scaleUnit,
        mousePos.y,
        offsetTop,
      )
      if (newDate.isSameOrAfter(selectedEvent.end, 'minutes')) {
        newDate = moment(selectedEvent.end).subtract(minutesStep, 'minutes')
      }
      if (
        !allowOverlap &&
        !newDate.isBetween(minMaxInterval.start, minMaxInterval.end, null, '[)')
      ) {
        newDate = selectedEvent.start
      }
      return {
        ...selectedEvent,
        start: newDate,
      }
    } else if (dragType === EDragType.Bottom) {
      let newDate = utils.getMoment(
        selectedEvent.start,
        scaleUnit,
        mousePos.y + 1,
        offsetTop,
      )
      if (newDate.isSameOrBefore(selectedEvent.start, 'minutes')) {
        newDate = moment(selectedEvent.start).add(minutesStep, 'minutes')
      }
      if (
        !allowOverlap &&
        !newDate.isBetween(minMaxInterval.start, minMaxInterval.end, null, '(]')
      ) {
        newDate = selectedEvent.end
      }
      return {
        ...selectedEvent,
        end: newDate,
      }
    }
  }

  const handleDragStop = () => {
    if (!preview) {
      submitPreselectedIntervalUpdate(
        selectedEvent.id,
        selectedEvent.start.toDate(),
        selectedEvent.end.toDate(),
        selectedEvent.comment,
        selectedEvent.placeId,
      )
      const foundIndex = selectedIntervalsState.findIndex(
        (interval) => interval.id === selectedEvent.id,
      )
      if (foundIndex !== -1) {
        const state = [...selectedIntervalsState]
        state[foundIndex] = selectedEvent
        setSelectedIntervalsState(state)
      }
      setDragType(undefined)
      setSelectedEvent(undefined)
      setStartDay(undefined)
      setMinMaxInterval(undefined)
    }
  }

  const handleSelection = (mousePos: IPos) => {
    if (!startSelectionPosition || !mousePos) {
      return
    }

    const minDayIndex = Math.min(startSelectionPosition.x, mousePos.x)
    const maxDayIndex = Math.max(startSelectionPosition.x, mousePos.x)

    const startDay = moment(firstDay).add(minDayIndex, 'days')
    const endDay = moment(firstDay).add(maxDayIndex, 'days')

    const minCellIndex = Math.min(startSelectionPosition.y, mousePos.y)
    const maxCellIndex = Math.max(startSelectionPosition.y, mousePos.y) + 1
    const startSelectionTime = utils.getMoment(
      startDay,
      scaleUnit,
      minCellIndex,
      offsetTop,
    )
    const endSelectionTime = utils.getMoment(
      startDay,
      scaleUnit,
      maxCellIndex,
      offsetTop,
    )

    const start = moment(startDay)
      .hour(startSelectionTime.hour())
      .minute(startSelectionTime.minute())
      .second(0)
    const end = moment(endDay)
      .hour(endSelectionTime.hour())
      .minute(endSelectionTime.minute())
      .second(0)
    setPreselectedInterval({ start, end })
  }

  const handleSelectionStop = () => {
    if (dragType && !preview) {
      return handleDragStop()
    }
    setSelectedPopup('ADD')
  }

  const submitPreselectedIntervalCreate = async (
    startTimestamp: Date,
    endTimestamp: Date,
    comment?: string,
    placeId?: number,
    exceptions?: IExceptions,
  ) => {
    const state = [...selectedIntervalsState]
    state.push({
      id: -1, // Not yet added to backend
      start: moment(startTimestamp),
      end: moment(endTimestamp),
      comment,
      color: createColor,
    })
    setSelectedIntervalsState(state)
    onIntervalSelect(startTimestamp, endTimestamp, comment, placeId, exceptions)
  }

  const submitPreselectedIntervalUpdate = (
    id: number,
    startTimestamp: Date,
    endTimestamp: Date,
    comment?: string,
    placeId?: number,
  ) => {
    onIntervalUpdate(id, startTimestamp, endTimestamp, comment, placeId)
    const foundIndex = selectedIntervalsState.findIndex(
      (interval) => interval.id === selectedEvent.id,
    )
    if (foundIndex !== -1) {
      const state = [...selectedIntervalsState]
      state[foundIndex] = {
        ...selectedEvent,
        start: moment(startTimestamp),
        end: moment(endTimestamp),
        comment,
      }
      setSelectedIntervalsState(state)
    }
  }

  const removePreselectedInterval = () => {
    if (selectedEvent && !preview && selectedEvent.id > -1) {
      onIntervalsRemove([selectedEvent.id])
    }
  }

  const calculateMinMax = (
    mouseYDate: moment.Moment,
    day: moment.Moment,
    ignoreIntervalId?: number,
  ) => {
    const dayIntervals = utils.getDayIntervals(day, scaleIntervals)
    let minAllowed = moment(dayIntervals[0].start)
    let maxAllowed = moment(dayIntervals[dayIntervals.length - 1].end)
    selectedIntervalsState.map((interval) => {
      if (
        (interval.start.isSame(day, 'day') && ignoreIntervalId === undefined) ||
        interval.id !== ignoreIntervalId
      ) {
        const intervalStart = moment(interval.start)
        const intervalEnd = moment(interval.end)

        if (
          intervalStart.isBefore(mouseYDate) &&
          intervalEnd.isAfter(minAllowed)
        ) {
          minAllowed = intervalEnd
        } else if (
          intervalStart.isAfter(mouseYDate) &&
          intervalStart.isBefore(maxAllowed)
        ) {
          maxAllowed = intervalStart
        }
      }
    })
    setMinMaxInterval({
      start: minAllowed,
      end: maxAllowed,
    })
    setStartDay(day)
  }

  const handleSelectionStart = (
    col: number,
    row: number,
    day: moment.Moment,
  ) => {
    if (!preview) {
      const newStartSelectionPosition = {
        x: col,
        y: row,
      }
      setStartSelectionPosition(newStartSelectionPosition)
      setMousePosition(newStartSelectionPosition)
      const dayIntervals = utils.getDayIntervals(day, scaleIntervals)
      const mouseYDate = utils.getMoment(
        dayIntervals[0].start,
        scaleUnit,
        row,
        offsetTop,
      )
      calculateMinMax(mouseYDate, day)
    }
  }

  const getAllowedMousePos = (newMousePosition: IPos): IPos => {
    const dayIntervals = utils.getDayIntervals(startDay, scaleIntervals)
    const mouseYDate = utils.getMoment(
      dayIntervals[0].start,
      scaleUnit,
      newMousePosition.y,
      offsetTop,
    )
    let actualPosition = newMousePosition
    if (
      !allowOverlap &&
      !mouseYDate.isBetween(
        minMaxInterval.start,
        minMaxInterval.end,
        null,
        '[)',
      )
    ) {
      actualPosition = mousePosition
    }
    return actualPosition
  }

  const handleCellMouseEnter = (_col: number, row: number) => {
    let newMousePosition = {
      x: mousePosition.x, // col,
      y: row,
    }
    if (startSelectionPosition) {
      newMousePosition = getAllowedMousePos(newMousePosition)
      setMousePosition(newMousePosition)
    }

    if (selectedEvent) {
      const newSelectedEvent = calculateDrag(newMousePosition)
      setSelectedEvent(newSelectedEvent)
    } else {
      handleSelection(newMousePosition)
    }
  }

  const renderInterval = (
    interval: IExistingScaleInterval,
    index: number,
    array: IExistingScaleInterval[],
    dayIndex: number,
    day: moment.Moment,
    noPointerEvent?: boolean,
  ): React.ReactNode => {
    let startY = 0
    if (!interval.start.isBefore(day)) {
      startY = utils.getNumberOfCells(
        interval.start,
        scaleUnit,
        false,
        offsetTop,
      )
    }

    if (startY > scaleIntervals.length) {
      return
    }

    let left = columnDimensions[dayIndex].left
    let width = columnDimensions[dayIndex].width - eventSpacing
    if (index >= 0) {
      const beforeIntersectionNumber = array.filter(
        (i, i1) => i1 < index && interval.start.isBefore(i.end),
      ).length
      const afterIntersectionNumber = array.filter(
        (i, i1) => i1 > index && interval.end.isAfter(i.start),
      ).length
      const groupIntersection =
        beforeIntersectionNumber + afterIntersectionNumber + 1

      width =
        (columnDimensions[dayIndex].width - eventSpacing) / groupIntersection

      // TODO: dividing by the GroupIntersection doesn't seem to work all that great...
      left =
        columnDimensions[dayIndex].left +
        (width + Math.floor(eventSpacing / groupIntersection)) *
          beforeIntersectionNumber
    }
    let endY = utils.getNumberOfCells(interval.end, scaleUnit, true, offsetTop)
    if (endY > scaleIntervals.length) {
      endY = scaleIntervals.length
    }
    const top = startY * cellHeight + mainOffsetTop
    const height = (endY - startY) * cellHeight

    const eventComponent = (
      <div
        className="weekCalendar__overlay"
        key={dayIndex * 20000 + index}
        style={{
          top,
          left,
          width,
          height,
          zIndex: 100,
          fontSize,
          pointerEvents: !noPointerEvent && !dragType ? 'auto' : 'none',
        }}
        onClick={() => {
          if (!dragType && !preview) {
            setSelectedEvent(interval)
            setSelectedPopup('EDIT')
            calculateMinMax(interval.start, day, interval.id)
          }
        }}
      >
        <Event
          {...interval}
          preview={preview}
          showPlace={showPlaceOnEvent}
          placeData={placeData}
          isSelected={interval === selectedEvent}
          firstDay={firstDay}
          onDragClick={(dragType) => {
            if (!preview) {
              setDragType(dragType)
              setSelectedEvent(interval)
              calculateMinMax(
                dragType === EDragType.Top ? interval.start : interval.end,
                day,
                interval.id,
              )
            }
          }}
        />
      </div>
    )
    return eventComponent
  }

  const renderSelectedIntervals = () => {
    const result: React.ReactNode[] = []
    if (columnDimensions.length === 0) {
      return result
    }

    // Existing intervals
    for (let dayIndex = 0; dayIndex < numberOfDays; dayIndex += 1) {
      const day = moment(firstDay).startOf('day').add(dayIndex, 'day')
      const intervals = selectedIntervalsState.filter(
        (interval) =>
          interval.start.isSame(day, 'day') || interval.end.isSame(day, 'day'),
      )
      if (intervals.length > 0) {
        intervals.sort((i1, i2) => i1.start.diff(i2.start, 'minutes'))

        intervals.forEach((interval, index, array) => {
          if (selectedEvent ? selectedEvent.id !== interval.id : true) {
            result.push(renderInterval(interval, index, array, dayIndex, day))
          }
        })
        if (selectedEvent && selectedEvent.start.isSame(day, 'day')) {
          result.push(
            renderInterval(selectedEvent, -1, intervals, dayIndex, day),
          )
        }
      }
      if (preselectedInterval && mousePosition.x === dayIndex) {
        result.push(
          renderInterval(
            {
              ...preselectedInterval,
              color: createColor,
            } as IExistingScaleInterval,
            -1,
            intervals,
            dayIndex,
            day,
            true,
          ),
        )
      }
    }
    return result
  }

  const handleChangeWeek = (next: boolean) => {
    if (next) {
      const newStartDay = moment(firstDay).add(1, 'week')
      if (!firstDay.isSameOrAfter(moment(maxDay), 'week')) {
        onFirstDayUpdate(newStartDay)
      }
    } else {
      const newStartDay = moment(firstDay).subtract(1, 'week')
      if (!firstDay.isSameOrBefore(moment(minDay), 'week')) {
        onFirstDayUpdate(newStartDay)
      }
    }
  }

  const handlers = useSwipeable({
    onSwipedLeft: () => handleChangeWeek(true),
    onSwipedRight: () => handleChangeWeek(false),
  })

  const handleClearWeek = () => {
    const lastDayInWeek = moment(firstDay).endOf('week')
    const ids = selectedIntervals.reduce((list, interval) => {
      if (interval.start.isBetween(firstDay, lastDayInWeek)) {
        list.push(interval.id)
      }
      return list
    }, [])
    onIntervalsRemove(ids)
  }

  return (
    <>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        marginTop={2}
        marginBottom={2}
      >
        <Grid item xs textAlign="left">
          <ActionButton
            label={!isMobileWidth && t('buttons.previuswWeek')}
            icon={<ChevronLeftIcon />}
            disabled={firstDay.isSame(moment(minDay), 'week')}
            onClick={() => handleChangeWeek(false)}
          />
        </Grid>
        <Grid item xs textAlign="center">
          <Button
            size="small"
            variant="contained"
            onClick={() => onFirstDayUpdate(moment(new Date()).startOf('week'))}
          >
            {t('buttons.today')}
          </Button>
        </Grid>
        <Grid item xs textAlign="center">
          <ActionButton
            label={!isMobileWidth && t('buttons.clearWeek')}
            icon={<DeleteIcon />}
            endIcon={true}
            isLoading={isLoading}
            disabled={!selectedIntervals.length}
            onClick={() => handleClearWeek()}
          />
        </Grid>
        <Grid item xs textAlign="right">
          <ActionButton
            label={!isMobileWidth && t('buttons.nextWeek')}
            icon={<ChevronRightIcon />}
            endIcon={true}
            disabled={firstDay.isSameOrAfter(moment(maxDay), 'week')}
            onClick={() => handleChangeWeek(true)}
          />
        </Grid>
      </Grid>
      <div
        {...handlers}
        id="weekCalendar"
        className={'weekCalendar'}
        style={{
          width: 'calc(100% + 60px)',
          marginLeft: '-30px',
          marginRight: '-30px',
          height: 'auto',
        }}
        onMouseUp={handleSelectionStop}
      >
        <div
          className="weekCalendar__header"
          style={{
            left: 0,
            height: cellHeight,
            paddingLeft: cellWidth,
            fontSize,
          }}
        >
          <CalendarHeader
            firstDay={firstDay}
            numberOfDays={numberOfDays}
            columnDimensions={columnDimensions}
          />
        </div>
        <div
          className="weekCalendar__header"
          style={{
            left: 0,
            top: cellHeight,
            height: dayCommentHeight,
            paddingLeft: cellWidth,
            fontSize,
          }}
        >
          <CalendarDayComments
            firstDay={firstDay}
            numberOfDays={numberOfDays}
            columnDimensions={columnDimensions}
            comments={calendarDayComment}
            height={dayCommentHeight}
            fontSize={fontSize}
            onChange={onChangeDayComment}
          />
        </div>
        <div
          className="weekCalendar__scaleColumn"
          style={{ top: mainOffsetTop, width: cellWidth, fontSize }}
        >
          <ScaleColumn
            scaleUnit={scaleUnit}
            scaleFormat={scaleFormat}
            scaleIntervals={scaleIntervals}
            cellHeight={cellHeight}
            cellWidth={cellWidth}
          />
        </div>
        <div
          className="weekCalendar__content"
          style={{ top: mainOffsetTop, paddingLeft: cellWidth }}
        >
          <CalendarBody
            preview={preview}
            firstDay={firstDay}
            minDay={minDay}
            maxDay={maxDay}
            numberOfDays={numberOfDays}
            scaleIntervals={scaleIntervals}
            cellHeight={cellHeight}
            cellWidth={cellWidth}
            cellConfigs={correctedCellConfigs}
            outsideRangeColor={outsideRangeColor}
            fontSize={fontSize}
            onSelectionStart={handleSelectionStart}
            onClick={(x, y) => handleSelection({ x, y })}
            onCellMouseEnter={handleCellMouseEnter}
          />
          {renderSelectedIntervals()}
        </div>
      </div>
      {preselectedInterval && selectedPopup === 'ADD' && (
        <PopupDialog
          title={t('popups.addTimeslot')}
          startTimestamp={preselectedInterval.start}
          endTimestamp={preselectedInterval.end}
          minAllowed={minMaxInterval && minMaxInterval.start}
          maxAllowed={minMaxInterval && minMaxInterval.end}
          firstDay={firstDay}
          scaleFormat={scaleFormat}
          selections={selections}
          scaleIntervals={utils.getDayIntervals(
            preselectedInterval.start,
            scaleIntervals,
          )}
          isLoading={isLoading}
          placeData={placeData}
          onClose={() => {
            setSelectedPopup(undefined)
            setPreselectedInterval(undefined)
            setStartSelectionPosition(undefined)
          }}
          onConfirm={submitPreselectedIntervalCreate}
        />
      )}
      {selectedEvent && selectedPopup === 'EDIT' && (
        <PopupDialog
          title={t('popups.editTimeslot')}
          startTimestamp={selectedEvent.start}
          endTimestamp={selectedEvent.end}
          comment={selectedEvent.comment}
          minAllowed={minMaxInterval && minMaxInterval.start}
          maxAllowed={minMaxInterval && minMaxInterval.end}
          firstDay={firstDay}
          scaleFormat={scaleFormat}
          scaleIntervals={utils.getDayIntervals(
            selectedEvent.start,
            scaleIntervals,
          )}
          selections={selections}
          isLoading={isLoading}
          placeData={placeData}
          preSelectedPlaceId={selectedEvent.placeId}
          onClose={() => {
            setSelectedPopup(undefined)
            setSelectedEvent(undefined)
          }}
          onDelete={removePreselectedInterval}
          onConfirm={(start, end, comment, placeId) =>
            submitPreselectedIntervalUpdate(
              selectedEvent.id,
              start,
              end,
              comment,
              placeId,
            )
          }
        />
      )}
    </>
  )
}

export default Calendar
